import { Link, createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { ScanUrlManager } from '@/components/scan-url-manager';
import { CrawlHistoryTable } from '@/components/crawl-history-table';
import { TriggerScanButton } from '@/components/trigger-scan-button';
import { BaselineStatusBadge } from '@/components/baseline-badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/format';
import { useCurrentIdentity } from '@/lib/identity-context';

export const siteDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId',
  component: SiteDetailPage,
});

function SiteDetailPage() {
  const { siteId } = siteDetailRoute.useParams();
  const { hasPermission } = useCurrentIdentity();
  const canViewDataFlows = hasPermission('data-flows:read');

  const siteQuery = useQuery({
    queryKey: queryKeys.sites.detail(siteId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}', { params: { path: { siteId } } });
      if (error) throw error;
      return data;
    },
  });

  const driftQuery = useQuery({
    queryKey: queryKeys.sites.drift(siteId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}/drift', { params: { path: { siteId } } });
      if (error) return null;
      return data;
    },
  });

  if (siteQuery.isLoading) return <p className="text-sm text-muted-foreground">Loading site…</p>;
  if (siteQuery.isError || !siteQuery.data) return <p className="text-sm text-red-600">Site not found.</p>;

  const { site, currentBaseline } = siteQuery.data;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{site.name}</h1>
          <a
            href={site.baseUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs text-muted-foreground underline underline-offset-4"
          >
            {site.baseUrl}
          </a>
          <div className="flex items-center gap-2 pt-1">
            <BaselineStatusBadge baseline={currentBaseline} drift={driftQuery.data ?? null} />
            {currentBaseline && (
              <span className="text-xs text-muted-foreground">
                Baseline approved {formatDateTime(currentBaseline.approvedAt)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canViewDataFlows && (
            <Link
              to="/sites/$siteId/data-flows"
              params={{ siteId: site.id }}
              className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              data-testid="site-data-flows-link"
            >
              Data flows
            </Link>
          )}
          <Link
            to="/sites/$siteId/diff"
            params={{ siteId: site.id }}
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            data-testid="site-diff-link"
          >
            Diff
          </Link>
          <Link
            to="/sites/$siteId/baseline"
            params={{ siteId: site.id }}
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            data-testid="site-baseline-link"
          >
            Baseline
          </Link>
          <Link
            to="/sites/$siteId/alerts"
            params={{ siteId: site.id }}
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            data-testid="site-alerts-link"
          >
            Alerts
          </Link>
          <TriggerScanButton siteId={site.id} size="default" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <ScanUrlManager siteId={site.id} />
        <Card>
          <CardHeader>
            <CardTitle>Site settings</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <Row label="Max pages">{site.settings?.maxPages ?? '—'}</Row>
            <Row label="Scan frequency">
              {site.settings?.scanFrequencyHours ? `${site.settings.scanFrequencyHours}h` : '—'}
            </Row>
            <Row label="Include patterns">{site.settings?.urlIncludePatterns?.length ?? 0}</Row>
            <Row label="Exclude patterns">{site.settings?.urlExcludePatterns?.length ?? 0}</Row>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Crawl history</CardTitle>
        </CardHeader>
        <CardContent>
          <CrawlHistoryTable siteId={site.id} />
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span>{children}</span>
    </div>
  );
}
